/**
 * 华为应用商店 → 策略配置（CLAUDE.md §前端 UI 须借鉴 LEO v1.0 §6.1/§6.3/§6.5；
 * 本页复用 leo-card / leo-input / leo-select / leo-cta-primary 类）。
 *
 * 行为：
 *   - 「目标费率与成本」卡片（PR-HW1-20261008-001，auto_huawei_store_feilv_target_config）：
 *     进入页面独立 GET /api/v1/huawei/feilv-target-config 拉取当前态（与策略表
 *     加载解耦，失败仅 toast 不影响策略表格；表无行 / DDL 未执行时后端 fail-open
 *     返回默认态 custom + 0.01 + 成本 4000）；目标费率当前仅 custom 手动固定值
 *     模式（(0,1) 开区间小数，如 0.01=1%）；目标成本 = 目标企业进件成本
 *     （元，(0,20000) 开区间，默认 4000），供后续策略阈值消费；权限键
 *     huawei:feilv-target（用户组同 huawei:strategy），无权限用户控件只读 + 提示。
 *   - 「自定义策略配置」卡片（盯盘策略，镜像 Apple Ads watch strategy 区块）：
 *     进入页面 GET /api/v1/huawei/watch-strategy 拉取全量策略行
 *     （任意登录用户可读）；新建/编辑/开关/删除受白名单控制（前端 canEdit 禁用 +
 *     后端 403 双层拦截）；状态切换使用药丸 Toggle 滑动开关；删除为软删除
 *     （is_deleted=1），行内「删除」按钮先弹确认框防误触（PR-HW-20261102-003）。
 *     列表/脑图双视图（PR-HW-20261013-001/002/003）：默认列表视图（六列表格，
 *     「状态」后为「策略id」列=auto_huawei_watch_strategy_config.id 自增主键）；
 *     「脑图」视图把全量策略合并为「条件前缀树 → 执行操作」（同层完全相同
 *     结点合并为一个，标注「启用 x/共 y」，全停用子树弱化），飞书画板式
 *     盒节点 + 贝塞尔连线、文本自适应换行、默认展开根以下 4 层、画布可
 *     拖拽平移、支持整屏全屏查看（「全屏」/「退出全屏」按钮 + ESC）；
 *     hover 浮层看合并策略明细；数值型条件与带参操作叶子的数值支持内联
 *     编辑（PR-HW-20261102-001，点击数值 → 输入框 → 确认弹窗批量应用合并
 *     结点途经的全部策略；指标名称/操作符/周期/动作类型不可编辑，无权限
 *     用户保持纯预览）（WatchStrategyMindMap）。分段切换独立一行（避免挤压标题栏）。
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type FeilvTargetConfigRecord,
  getFeilvTargetConfig,
  saveFeilvTargetConfig,
} from "../../services/huawei/feilvTargetConfig";
import {
  type WatchStrategyRecord,
  type SaveWatchStrategyBody,
  listWatchStrategies,
  createWatchStrategy,
  updateWatchStrategy,
  toggleWatchStrategy,
  deleteWatchStrategy,
  describeCondition,
  describeAction,
} from "../../services/huawei/watchStrategy";
import WatchStrategyEditor from "../../components/huaweiWatchStrategy/WatchStrategyEditor";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import WatchStrategyMindMap from "../../components/huaweiWatchStrategy/WatchStrategyMindMap";
import {
  type MindMapNode,
  validateNodeValue,
  buildConditionValuePatchBodies,
  buildActionValuePatchBodies,
} from "../../components/huaweiWatchStrategy/strategyMindMap";
import { pushToast } from "../../hooks/useToast";
import { useAuth } from "../../contexts/AuthContext";
import { PAGE_KEYS } from "../../config/permissions";

/** 库内态缺失（GET 失败/未加载）时的默认基线值（与后端 DEFAULT_FEILV_VALUE 一致）。 */
const DEFAULT_FEILV_VALUE = 0.01;

/** 库内态缺失时的默认目标企业进件成本（元，与后端 DEFAULT_COST_VALUE 一致）。 */
const DEFAULT_COST_VALUE = 4000;

/** 执行操作徽章样式（语义描边色，镜像 Apple Ads watchActionTagClass）。 */
function watchActionTagClass(actionType: string): string {
  if (actionType === "stop_adgroup") return "leo-tag is-danger";
  if (actionType === "reduce_budget" || actionType === "warning") return "leo-tag is-warning";
  return "leo-tag is-info";
}

export default function StrategyConfigPage() {
  const { canEdit } = useAuth();
  // 目标费率与成本：独立权限键 huawei:feilv-target（用户组同 huawei:strategy + 管理员）。
  const canOperateFeilv = canEdit(PAGE_KEYS.HUAWEI_FEILV_TARGET);
  // 目标费率与成本配置：record 为库内当前态；valueDraft / costDraft 为本地编辑态。
  const [feilvRecord, setFeilvRecord] = useState<FeilvTargetConfigRecord | null>(null);
  const [feilvValueDraft, setFeilvValueDraft] = useState(String(DEFAULT_FEILV_VALUE));
  const [costValueDraft, setCostValueDraft] = useState(String(DEFAULT_COST_VALUE));
  const [feilvSaving, setFeilvSaving] = useState(false);
  /** 盯盘策略：自定义策略配置（镜像 Apple Ads watch strategy 区块）。 */
  const canEditWatch = canEdit(PAGE_KEYS.HUAWEI_WATCH_STRATEGY);
  const [watchStrategies, setWatchStrategies] = useState<WatchStrategyRecord[]>([]);
  const [watchLoading, setWatchLoading] = useState(true);
  const [watchSaving, setWatchSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorRecord, setEditorRecord] = useState<WatchStrategyRecord | null>(null);
  /** 删除确认弹窗：待删策略行 + 提交中标记（PR-HW-20261102-003）。 */
  const [deletingRecord, setDeletingRecord] = useState<WatchStrategyRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** 策略视图：list=六列表格（编辑主链路）；mindmap=条件前缀树脑图（数值可内联编辑）。 */
  const [strategyView, setStrategyView] = useState<"list" | "mindmap">("list");

  /** 目标费率与成本独立加载：与盯盘策略表解耦——失败仅 toast，不影响盯盘策略表格。 */
  const loadFeilv = useCallback(async () => {
    try {
      const r = await getFeilvTargetConfig();
      setFeilvRecord(r);
      if (r) {
        setFeilvValueDraft(String(r.feilv_value));
        setCostValueDraft(String(r.cost_value));
      }
    } catch (err) {
      pushToast(
        err instanceof ApiError ? err.message : "拉取目标费率与成本配置失败",
        "error",
      );
      setFeilvRecord(null);
    }
  }, []);

  useEffect(() => {
    loadFeilv();
  }, [loadFeilv]);

  async function onSaveFeilv() {
    // feilvRecord 允许为 null（GET 失败/未加载时按默认基线 custom+0.01+成本4000 保存），
    // 不因读取失败永久锁死保存按钮。
    if (feilvSaving || !canOperateFeilv) return;
    const parsed = Number.parseFloat(feilvValueDraft);
    if (!(Number.isFinite(parsed) && parsed > 0 && parsed < 1)) {
      pushToast("目标费率须为 (0,1) 开区间小数（如 0.01 表示 1%）", "error");
      return;
    }
    const parsedCost = Number.parseFloat(costValueDraft);
    if (!(Number.isFinite(parsedCost) && parsedCost > 0 && parsedCost < 20000)) {
      pushToast("目标成本须为 (0,20000) 开区间的金额（元，如 4000）", "error");
      return;
    }
    setFeilvSaving(true);
    try {
      const r = await saveFeilvTargetConfig({
        feilv_mode: "custom",
        feilv_value: parsed,
        cost_value: parsedCost,
      });
      setFeilvRecord(r);
      setFeilvValueDraft(String(r.feilv_value));
      setCostValueDraft(String(r.cost_value));
      pushToast(
        `目标费率已保存为固定值 ${r.feilv_value}，目标成本 ${r.cost_value} 元`,
        "success",
      );
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "保存目标费率与成本配置失败",
        "error",
      );
    } finally {
      setFeilvSaving(false);
    }
  }

  /** 盯盘策略数据加载（独立于目标费率，失败 toast 不阻断）；列表页不限速全量拉取。 */
  const loadWatchStrategies = useCallback(async () => {
    setWatchLoading(true);
    try {
      const result = await listWatchStrategies(1, 200);
      setWatchStrategies(result.items);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "拉取自定义盯盘策略失败";
      pushToast(msg, "error");
      setWatchStrategies([]);
    } finally {
      setWatchLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWatchStrategies();
  }, [loadWatchStrategies]);

  function openCreateEditor() {
    setEditorRecord(null);
    setEditorOpen(true);
  }

  function openEditEditor(rec: WatchStrategyRecord) {
    setEditorRecord(rec);
    setEditorOpen(true);
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditorRecord(null);
  }

  async function handleWatchSubmit(body: SaveWatchStrategyBody) {
    if (watchSaving) return;
    setWatchSaving(true);
    try {
      if (editorRecord) {
        await updateWatchStrategy(editorRecord.id, body);
        pushToast(`盯盘策略 #${editorRecord.id} 已更新`, "success");
      } else {
        await createWatchStrategy(body);
        pushToast("盯盘策略已新建（默认打开）", "success");
      }
      await loadWatchStrategies();
      closeEditor();
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "保存盯盘策略失败",
        "error",
      );
    } finally {
      setWatchSaving(false);
    }
  }

  /**
   * 脑图数值内联编辑提交（PR-HW-20261102-001）：合并结点 = 批量应用——
   * 对途经（条件结点）/ 归属（操作叶子）该结点的全部策略逐条 PUT 更新对应数值，
   * 条件组其余字段与操作原样；结束后重拉全量（脑图树自动重建）。
   */
  async function handleMindMapValueSubmit(node: MindMapNode, newValue: number) {
    // 组件已做一轮校验，此处防御性复核（非法直接 toast，不产生任何请求）
    const err = validateNodeValue(node, newValue);
    if (err) {
      pushToast(err, "error");
      return;
    }
    const patches =
      node.editable?.kind === "condition_value"
        ? buildConditionValuePatchBodies(node, newValue)
        : buildActionValuePatchBodies(node, newValue);
    const results = await Promise.allSettled(
      patches.map((p) => updateWatchStrategy(p.id, p.body)),
    );
    const okCount = results.filter((r) => r.status === "fulfilled").length;
    const failCount = results.length - okCount;
    if (failCount === 0) {
      pushToast(`已更新 ${okCount} 条策略的数值`, "success");
    } else {
      pushToast(`数值更新完成：成功 ${okCount} 条，失败 ${failCount} 条`, "error");
    }
    await loadWatchStrategies();
  }

  /** 删除策略（DELETE /{id}，软删除）：确认弹窗 → 删除成功后本地剔除该行（不重拉全量，
   *  脑图随 watchStrategies 自动重建）。 */
  async function handleDeleteWatch() {
    if (!deletingRecord || deleting || !canEditWatch) return;
    setDeleting(true);
    try {
      await deleteWatchStrategy(deletingRecord.id);
      setWatchStrategies((prev) => prev.filter((it) => it.id !== deletingRecord.id));
      pushToast(`盯盘策略 #${deletingRecord.id} 已删除`, "success");
      setDeletingRecord(null);
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "删除盯盘策略失败",
        "error",
      );
    } finally {
      setDeleting(false);
    }
  }

  /** 打开 / 关闭策略（PUT /{id}/status），成功后用返回的当前态更新本地行（乐观更新，不重拉全量）。 */
  async function handleToggle(record: WatchStrategyRecord) {
    if (!canEditWatch || togglingId !== null) return;
    setTogglingId(record.id);
    try {
      const saved = await toggleWatchStrategy(record.id, !record.is_enabled);
      setWatchStrategies((prev) => prev.map((it) => (it.id === saved.id ? saved : it)));
      pushToast(saved.is_enabled ? `策略 #${saved.id} 已打开` : `策略 #${saved.id} 已关闭`, "success");
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "切换盯盘策略状态失败",
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  }

  // ----- 目标费率与成本派生态：固定值合法性 / 是否有未保存修改 -----
  const parsedDraft = Number.parseFloat(feilvValueDraft);
  const isDraftValid =
    Number.isFinite(parsedDraft) && parsedDraft > 0 && parsedDraft < 1;
  const feilvInvalid = !isDraftValid;
  const parsedCostDraft = Number.parseFloat(costValueDraft);
  const isCostDraftValid =
    Number.isFinite(parsedCostDraft) && parsedCostDraft > 0 && parsedCostDraft < 20000;
  const costInvalid = !isCostDraftValid;
  // 比对基线：库内态缺失（GET 失败/未加载）时按默认态 0.01 / 4000，
  // 保证「做修改后即可保存」不被读取失败锁死。
  const feilvBaselineValue = feilvRecord?.feilv_value ?? DEFAULT_FEILV_VALUE;
  const costBaselineValue = feilvRecord?.cost_value ?? DEFAULT_COST_VALUE;
  const feilvDirty = isDraftValid && parsedDraft !== feilvBaselineValue;
  const costDirty = isCostDraftValid && parsedCostDraft !== costBaselineValue;
  const configDirty = feilvDirty || costDirty;
  const configInvalid = feilvInvalid || costInvalid;

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
            策略配置
          </h1>
          <p className="mt-1 text-[12.5px] text-text-muted">
            华为应用商店盯盘策略：目标费率与成本配置 + 自定义盯盘服务（条件组 AND
            触发操作），策略配置由判断引擎周期性消费。
          </p>
        </div>
      </div>

      {/* 目标费率与成本卡片（LEO §6.3 表单控件：leo-input 深底 + focus 橙；保存为本卡片
          唯一主 CTA §6.1）。 */}
      <div className="leo-card mb-4 rounded-xl p-6">
        <div className="min-w-0">
          <div className="font-display text-[1.05rem] font-semibold text-text-emphasis">
            目标费率与成本
          </div>
          <div className="mt-1.5 text-[12.5px] text-text-muted">
            华为应用商店程序化控停策略的目标配置（feilv_target）。目标费率手动输入固定值
            （(0,1) 开区间小数，如 0.01 表示 1%）；目标成本 = 目标企业进件成本
            （元，(0,20000) 开区间），供后续策略阈值消费。
          </div>
        </div>

        {/* 「目标费率：[输入框]」「目标成本：[输入框] 元」固定同行：子组 shrink-0 内部
            不换行；输入框经定宽容器包裹——.leo-input 的 width:100% 在构建产物中位于
            Tailwind w-* 之后（同优先级后者胜），直接设宽度会被撑满整行。 */}
        <div className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-2">
          <div className="flex shrink-0 items-center gap-2.5">
            <span className="text-[12.5px] text-text-muted">目标费率：</span>
            <div className="w-28 shrink-0">
              <input
                type="text"
                inputMode="decimal"
                className="leo-input"
                value={feilvValueDraft}
                placeholder="如 0.01"
                readOnly={!canOperateFeilv}
                onChange={(e) => setFeilvValueDraft(e.target.value)}
                aria-label="目标费率"
              />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            <span className="text-[12.5px] text-text-muted">目标成本：</span>
            <div className="w-28 shrink-0">
              <input
                type="text"
                inputMode="decimal"
                className="leo-input"
                value={costValueDraft}
                placeholder="如 4000"
                readOnly={!canOperateFeilv}
                onChange={(e) => setCostValueDraft(e.target.value)}
                aria-label="目标成本（元）"
              />
            </div>
            <span className="text-[12.5px] text-text-muted">元</span>
          </div>
          <button
            type="button"
            className="leo-cta-primary ml-1 w-auto px-7"
            onClick={onSaveFeilv}
            disabled={!canOperateFeilv || feilvSaving || !configDirty || configInvalid}
          >
            {feilvSaving ? "保存中…" : "保存"}
          </button>
          {feilvInvalid ? (
            <span className="text-[11.5px] text-semantic-danger">
              目标费率须为 (0,1) 开区间小数（如 0.01）
            </span>
          ) : null}
          {costInvalid ? (
            <span className="text-[11.5px] text-semantic-danger">
              目标成本须为 (0,20000) 开区间（元，如 4000）
            </span>
          ) : null}
        </div>

        {/* 审计行 */}
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line-subtle pt-4 text-[12px] text-text-muted">
          {feilvRecord?.modify_user ? (
            <span>
              最近修改：<span className="text-text-strong">{feilvRecord.modify_user}</span>
              <span className="mx-1.5 text-text-muted-weak">·</span>
              <span className="font-mono">{feilvRecord.updated_time ?? "—"}</span>
            </span>
          ) : (
            <span>尚无修改记录（默认 custom 0.01 / 成本 4000 元）</span>
          )}
          {feilvRecord?.create_user ? (
            <span>
              首次创建：<span className="text-text-strong">{feilvRecord.create_user}</span>
            </span>
          ) : null}
        </div>

        {/* 权限提示 */}
        {!canOperateFeilv ? (
          <div className="mt-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
            仅 devinzhang / reizhu / v_summerxiao 可操作目标费率与成本；当前账号为只读。
          </div>
        ) : null}
      </div>

      {/* 自定义盯盘策略配置卡片（LEO §6.3 表单控件 / §6.6 弹窗浮层）；
          镜像 Apple Ads watch strategy 区块：新建盯盘服务按钮 + 六列表格 + 编辑器弹层。 */}
      <div className="leo-card mb-4 rounded-xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display text-[1.05rem] font-semibold text-text-emphasis">
              自定义策略配置
            </div>
            <div className="mt-1.5 text-[12.5px] text-text-muted">
              满足全部条件（且）时触发操作，策略配置由判断引擎周期性消费。仅「打开」状态的策略参与判断。
            </div>
          </div>
          <button
            type="button"
            className="leo-cta-primary w-auto shrink-0 px-6"
            onClick={openCreateEditor}
            disabled={!canEditWatch || watchLoading}
          >
            新建盯盘服务
          </button>
        </div>

        {/* 列表/脑图分段切换（复用 .leo-toptab pill 形态，LEO §6.2；独立一行，
            避免与标题/新建按钮挤占标题栏导致按钮被推出可视区） */}
        <div
          className="mt-4 flex w-fit items-center gap-0.5 rounded-full bg-da-inset p-0.5"
          role="tablist"
          aria-label="策略视图切换"
        >
          <button
            type="button"
            role="tab"
            aria-selected={strategyView === "list"}
            className={`leo-toptab ${strategyView === "list" ? "is-active" : ""}`}
            onClick={() => setStrategyView("list")}
          >
            列表
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={strategyView === "mindmap"}
            className={`leo-toptab ${strategyView === "mindmap" ? "is-active" : ""}`}
            onClick={() => setStrategyView("mindmap")}
          >
            脑图
          </button>
        </div>

        {/* 权限提示 */}
        {!canEditWatch ? (
          <div className="mt-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
            仅 devinzhang / reizhu / v_summerxiao / admin 可操作盯盘策略；当前账号为只读。
          </div>
        ) : null}

        {/* 策略列表表格 / 脑图（loading 与空态两视图共用，分叉在非空分支） */}
        <div className="mt-4 overflow-x-auto">
          {watchLoading ? (
            <div className="py-8 text-center text-[13px] text-text-muted">加载中…</div>
          ) : watchStrategies.length === 0 ? (
            <div className="py-8 text-center text-[12.5px] text-text-muted">
              暂无自定义盯盘策略，可点击「新建盯盘服务」创建
            </div>
          ) : strategyView === "mindmap" ? (
            /* 脑图视图：条件前缀树 → 执行操作（基线口径与表格列一致；数值型条件与
               带参操作叶子的数值支持内联编辑，批量应用合并结点途经的全部策略） */
            <WatchStrategyMindMap
              strategies={watchStrategies}
              targetSxfl={feilvRecord?.feilv_value ?? DEFAULT_FEILV_VALUE}
              targetCost={feilvRecord?.cost_value ?? DEFAULT_COST_VALUE}
              canEditValue={canEditWatch}
              onSubmitNodeValue={handleMindMapValueSubmit}
            />
          ) : (
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-line-strong">
                  <th className="px-2.5 py-2 text-left font-medium text-text-muted whitespace-nowrap w-16">
                    状态
                  </th>
                  <th className="px-2.5 py-2 text-left font-medium text-text-muted whitespace-nowrap w-16">
                    策略id
                  </th>
                  <th className="px-2.5 py-2 text-left font-medium text-text-muted whitespace-nowrap">
                    满足条件
                  </th>
                  <th className="px-2.5 py-2 text-left font-medium text-text-muted whitespace-nowrap w-28">
                    执行操作
                  </th>
                  <th className="px-2.5 py-2 text-left font-medium text-text-muted whitespace-nowrap w-20">
                    创建者
                  </th>
                  <th className="px-2.5 py-2 text-left font-medium text-text-muted whitespace-nowrap w-20">
                    编辑
                  </th>
                </tr>
              </thead>
              <tbody>
                {watchStrategies.map((s) => {
                  const feilvBaseline = feilvRecord?.feilv_value ?? DEFAULT_FEILV_VALUE;
                  const costBaseline = feilvRecord?.cost_value ?? DEFAULT_COST_VALUE;
                  const toggleDisabled = !canEditWatch || togglingId !== null;
                  return (
                    <tr key={s.id} className="border-b border-white/5 last:border-b-0">
                      {/* 状态：药丸 Toggle（LEO §6.3：is-on 填充强调色橙），镜像 Apple Ads watch strategy 区块。 */}
                      <td className="px-2.5 py-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={s.is_enabled}
                          aria-label={`策略 #${s.id} 开关`}
                          onClick={() => handleToggle(s)}
                          disabled={toggleDisabled}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-150 ${
                            s.is_enabled ? "bg-accent-cta" : "bg-text-muted-weak"
                          } ${toggleDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-150 ${
                              s.is_enabled ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                      {/* 策略id：auto_huawei_watch_strategy_config.id（自增主键），等宽字体便于与库表核对。 */}
                      <td className="px-2.5 py-2 font-mono text-text-muted whitespace-nowrap">
                        {s.id}
                      </td>
                      {/* 满足条件：条件组中文文本，多条件换行以「且」连接，镜像 Apple Ads watch strategy 区块。 */}
                      <td className="px-2.5 py-2 whitespace-normal">
                        <div className="flex flex-col gap-0.5">
                          {s.conditions.map((c, i) => (
                            <span key={i}>
                              {i > 0 ? (
                                <span className="mr-1 text-text-muted-weak">且</span>
                              ) : null}
                              {describeCondition(c, feilvBaseline, costBaseline)}
                            </span>
                          ))}
                        </div>
                      </td>
                      {/* 执行操作：语义徽章，镜像 Apple Ads watchActionTagClass。 */}
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        <span className={watchActionTagClass(s.action_type)}>
                          {describeAction(s.action_type, s.action_value)}
                        </span>
                      </td>
                      <td className="px-2.5 py-2 text-text-muted">
                        {s.create_user ?? "—"}
                      </td>
                      {/* 操作：编辑 + 删除（软删除，删除前弹确认框防误触）。 */}
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        <button
                          type="button"
                          className="leo-btn-ghost px-2 py-1 text-[12px]"
                          onClick={() => openEditEditor(s)}
                          disabled={!canEditWatch}
                        >
                          编辑
                        </button>
                        <button
                          type="button"
                          className="leo-btn-ghost is-danger ml-1.5 px-2 py-1 text-[12px]"
                          onClick={() => setDeletingRecord(s)}
                          disabled={!canEditWatch}
                        >
                          删除
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 盯盘策略编辑器弹层（新建 / 编辑；LEO §6.6 弹窗浮层） */}
      <WatchStrategyEditor
        open={editorOpen}
        record={editorRecord}
        saving={watchSaving}
        targetFeilv={feilvRecord?.feilv_value ?? DEFAULT_FEILV_VALUE}
        targetCost={feilvRecord?.cost_value ?? DEFAULT_COST_VALUE}
        onSubmit={handleWatchSubmit}
        onCancel={closeEditor}
      />

      {/* 删除确认弹窗（防误触；软删除，判断引擎不再消费该策略） */}
      <ConfirmDialog
        open={deletingRecord !== null}
        title="确认删除策略"
        description={
          <>
            确认删除策略{" "}
            <span className="font-mono font-semibold text-text-emphasis">
              #{deletingRecord?.id}
            </span>
            ？删除后判断引擎将不再消费该策略（软删除，库内保留记录可由管理员恢复）。
          </>
        }
        confirmText="确认删除"
        danger
        loading={deleting}
        onConfirm={handleDeleteWatch}
        onCancel={() => setDeletingRecord(null)}
      />
    </div>
  );
}